import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useStore } from 'hooks/useStore';
import { generate15MonthPeriods, periodToLabel } from 'data/fdp/oocMockData';
import { OOCTabType, ProbabilityLevel, OOCNDCSearchResult } from '../oocUpdated.type';

export { periodToLabel };

export const TAB_CONFIG: { key: OOCTabType; label: string }[] = [
  { key: 'MAS', label: 'MAS DP' },
  { key: 'NC', label: 'NC DP' },
  { key: 'ProvMAS', label: 'Prov. MAS' },
  { key: 'ProvNC', label: 'Prov. NC' },
];

export const TARGET_SECTION_OPTIONS: { label: string; value: OOCTabType }[] = [
  { label: 'MAS DP', value: 'MAS' },
  { label: 'NC DP', value: 'NC' },
  { label: 'Prov. MAS', value: 'ProvMAS' },
  { label: 'Prov. NC', value: 'ProvNC' },
];

export const WORKFLOW_STEPS = [
  { label: 'Draft', step: 1 },
  { label: 'Submitted / Communicated', step: 2 },
  { label: 'Integrated', step: 3 },
];

export const PROBABILITY_OPTIONS: { label: string; value: ProbabilityLevel }[] = [
  { label: 'Low', value: 'Low' },
  { label: 'Medium', value: 'Medium' },
  { label: 'High', value: 'High' },
];

export const getFcColorClass = (colorType: string, styles: Record<string, string>): string => {
  switch (colorType) {
    case 'new':
      return styles.fcNew;
    case 'modified':
      return styles.fcModified;
    case 'removed':
      return styles.fcRemoved;
    default:
      return styles.fcUnchanged;
  }
};

export const getStatusBadgeClass = (status: string, styles: Record<string, string>): string => {
  switch (status) {
    case 'draft':
      return styles.statusDraft;
    case 'communicated':
      return styles.statusCommunicated;
    case 'integrated':
      return styles.statusIntegrated;
    default:
      return styles.statusDraft;
  }
};

export const getStatusLabel = (status: string): string => {
  switch (status) {
    case 'draft':
      return 'Draft';
    case 'communicated':
      return 'Submitted / Communicated';
    case 'integrated':
      return 'Integrated';
    default:
      return status;
  }
};

export const useOOCCreateEditLogic = () => {
  const { OOCStoreUpdated: OOCStore, UserStore } = useStore();

  const [showSearchResults, setShowSearchResults] = useState(false);
  const [selectedNDCs, setSelectedNDCs] = useState<Set<string>>(new Set());

  const searchRef = useRef<{ timer: ReturnType<typeof setTimeout> | null; ctrl: AbortController | null }>({
    timer: null,
    ctrl: null,
  });

  useEffect(() => {
    const ref = searchRef.current;
    return () => {
      if (ref.timer !== null) clearTimeout(ref.timer);
      ref.ctrl?.abort();
    };
  }, []);

  const periods = useMemo(() => generate15MonthPeriods(), []);

  const submittedBy = UserStore.userMetadata?.userInfo?.name ?? '';

  const planningPeriod = useMemo(() => {
    const now = new Date();
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    return `${months[now.getMonth()]} ${now.getFullYear()}`;
  }, []);

  const moleculeGroups = useMemo(() => {
    const groups: Record<string, OOCNDCSearchResult[]> = {};
    OOCStore.ndcSearchResults.forEach((ndc) => {
      if (!groups[ndc.molecule]) groups[ndc.molecule] = [];
      groups[ndc.molecule].push(ndc);
    });
    return groups;
  }, [OOCStore.ndcSearchResults]);

  const showProbability = OOCStore.activeTab === 'ProvMAS';

  const handleSearchChange = useCallback((value: string) => {
    OOCStore.setNDCSearchQuery(value);
    setSelectedNDCs(new Set());

    const s = searchRef.current;
    if (s.timer !== null) { clearTimeout(s.timer); s.timer = null; }
    s.ctrl?.abort();
    s.ctrl = null;

    if (value.trim().length < 2) {
      setShowSearchResults(false);
      return;
    }

    s.timer = setTimeout(async () => {
      s.timer = null;
      const ctrl = new AbortController();
      s.ctrl = ctrl;
      try {
        await OOCStore.searchNDCs(ctrl.signal);
        setShowSearchResults(true);
      } catch {
        // Request was cancelled by a newer keystroke or failed — do nothing
      }
    }, 500);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // [] is intentional: OOCStore is a stable singleton, setters are stable

  const handleSearchKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key !== 'Enter' || OOCStore.ndcSearchQuery.trim().length < 2) return;

    const s = searchRef.current;
    if (s.timer !== null) { clearTimeout(s.timer); s.timer = null; }
    s.ctrl?.abort();
    const ctrl = new AbortController();
    s.ctrl = ctrl;
    OOCStore.searchNDCs(ctrl.signal)
      .then(() => setShowSearchResults(true))
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // [] is intentional: same reasoning as handleSearchChange

  const handleToggleNDCCheckbox = useCallback((ndc11: string) => {
    setSelectedNDCs((prev) => {
      const next = new Set(prev);
      if (next.has(ndc11)) next.delete(ndc11);
      else next.add(ndc11);
      return next;
    });
  }, []);

  const handleToggleMoleculeCheckbox = useCallback(
    (molecule: string) => {
      const ndcsInGroup = (moleculeGroups[molecule] ?? []).map((n) => n.ndc11);
      setSelectedNDCs((prev) => {
        const next = new Set(prev);
        const allChecked = ndcsInGroup.every((n) => next.has(n));
        if (allChecked) ndcsInGroup.forEach((n) => next.delete(n));
        else ndcsInGroup.forEach((n) => next.add(n));
        return next;
      });
    },
    [moleculeGroups]
  );

  const isMoleculeFullyChecked = useCallback(
    (molecule: string): boolean => {
      const ndcs = moleculeGroups[molecule] ?? [];
      return ndcs.length > 0 && ndcs.every((n) => selectedNDCs.has(n.ndc11));
    },
    [moleculeGroups, selectedNDCs]
  );

  const isMoleculeIndeterminate = useCallback(
    (molecule: string): boolean => {
      const ndcs = moleculeGroups[molecule] ?? [];
      const checkedCount = ndcs.filter((n) => selectedNDCs.has(n.ndc11)).length;
      return checkedCount > 0 && checkedCount < ndcs.length;
    },
    [moleculeGroups, selectedNDCs]
  );

  const [isAddingNDC, setIsAddingNDC] = useState(false);

  const handleAddNDCButtonClick = useCallback(async () => {
    if (selectedNDCs.size === 0) {
      if (OOCStore.ndcSearchQuery.trim().length >= 2) {
        await OOCStore.searchNDCs();
        setShowSearchResults(true);
      }
      return;
    }

    const ndcsToAdd = OOCStore.ndcSearchResults.filter((n) => selectedNDCs.has(n.ndc11));
    const ndc11s = ndcsToAdd.map((n) => n.ndc11);
    const firstTab = ndcsToAdd[0]?.suggestedTab;

    OOCStore.setLoadingBaseline(ndc11s);
    setIsAddingNDC(true);

    let firstAdded = false;
    for (const ndc of ndcsToAdd) {
      await OOCStore.addNDCToCurrentSubmission(ndc, ndc.suggestedTab);
      if (ndc.planningType === 'Provisional') {
        OOCStore.fetchProvForecastParams(ndc.ndc11, ndc.suggestedTab as 'ProvMAS' | 'ProvNC');
      }
      if (!firstAdded) {
        setIsAddingNDC(false);
        firstAdded = true;
      }
    }

    if (firstTab) OOCStore.setActiveTab(firstTab);

    setSelectedNDCs(new Set());
    OOCStore.setNDCSearchQuery('');
    setShowSearchResults(false);

    OOCStore.fetchAndApplyBaseline(ndc11s);
  }, [selectedNDCs, OOCStore]);

  const handleSearchBlur = useCallback(() => {
    setTimeout(() => setShowSearchResults(false), 300);
  }, []);

  const handleSearchFocus = useCallback(() => {
    if (OOCStore.ndcSearchQuery.trim().length >= 2 && OOCStore.ndcSearchResults.length > 0) {
      setShowSearchResults(true);
    }
  }, [OOCStore]);

  return {
    OOCStore,
    periods,
    submittedBy,
    planningPeriod,
    moleculeGroups,
    showProbability,
    showSearchResults,
    setShowSearchResults,
    selectedNDCs,
    handleSearchChange,
    handleSearchKeyDown,
    handleToggleNDCCheckbox,
    handleToggleMoleculeCheckbox,
    isMoleculeFullyChecked,
    isMoleculeIndeterminate,
    isAddingNDC,
    handleAddNDCButtonClick,
    handleSearchBlur,
    handleSearchFocus,
  };
};
